<!-- Bar & Column (BAR): ONE builder → the whole family. Orientation toggle
     (Horizontal/Vertical) + variant picker (Simple/Grouped/Stacked/100%/
     Clustered) decide how the mapped category + measures render. -->
<div class="wf-section wf-section-plain" *ngIf="active">
  <ng-container *ngTemplateOutlet="datasetPicker"></ng-container>

  <!-- Family card — mirrors the "Chart configuration" card style (icon + divider
       heading, padded white card) so the two sections read consistently. -->
  <div class="bridge-chart-settings bar-family-card">
    <div class="bridge-settings-heading"><mat-icon>bar_chart</mat-icon><span>{{ host.layoutSectionTitle('bar', 'Bar & column') }}</span></div>
    <small class="control-hint" *ngIf="host.layoutSectionHint('bar')">{{ host.layoutSectionHint('bar') }}</small>

    <!-- Orientation toggle -->
    <div class="control-group bar-orientation-field" *ngIf="host.fieldEnabled('bar', 'orientation')">
      <label>{{ host.fieldLabel('bar', 'orientation', 'Orientation') }}</label>
      <div class="wf-step-mode-toggle" role="group" aria-label="Bar orientation">
        <button type="button" [class.is-active]="barOrientation === 'vertical'" (click)="barOrientation = 'vertical'; onBarChange()">
          <mat-icon>bar_chart</mat-icon> Vertical
        </button>
        <button type="button" [class.is-active]="barOrientation === 'horizontal'" (click)="barOrientation = 'horizontal'; onBarChange()">
          <mat-icon>align_horizontal_left</mat-icon> Horizontal
        </button>
      </div>
    </div>

    <!-- Variant — its selection decides what the builder needs (Simple = no
         split; Grouped/Clustered/Stacked/100% = a required Split column). -->
    <div class="control-group" *ngIf="host.fieldEnabled('bar', 'variant')">
      <label>{{ host.fieldLabel('bar', 'variant', 'Variant') }}</label>
      <ng-select
        #ssBarVariant
        class="ss-select"
        [class.is-open]="!!ssBarVariant.isOpen?.()"
        appendTo="body"
        ngClass="ss-dropdown-panel"
        [items]="barVariants"
        bindLabel="label"
        bindValue="value"
        [clearable]="true"
        [ngModel]="barVariant"
        (ngModelChange)="barVariant = $event ?? ''; onBarChange()"
      ></ng-select>
      <small class="control-hint wf-variant-hint" *ngIf="barVariantHint">{{ barVariantHint }}</small>
    </div>
  </div>

  <!-- ══ Chart Data accordion — axis / measure / series mapping ══ -->
  <div class="bar-options-accordion">
    <div class="dvm-format-group" [class.is-open]="barDataAccordionOpen">
      <button type="button" class="dvm-format-group-head" (click)="barDataAccordionOpen = !barDataAccordionOpen">
        <span>Chart Data</span>
        <mat-icon>{{ barDataAccordionOpen ? 'expand_less' : 'expand_more' }}</mat-icon>
      </button>
      <div class="dvm-format-group-body" *ngIf="barDataAccordionOpen">

  <div class="control-row">
    <!-- Category = the group axis (X when vertical, Y when horizontal). -->
    <div class="control-group wf-field-drop-zone" *ngIf="host.fieldEnabled('bar', 'category')" (dragover)="host.allowFieldDrop($event)" (drop)="onBarCategoryFieldDrop($event)" title="Drag a column here">
      <label>{{ barCategoryAxisLabel }} <span class="wf-axis-hint">— group by</span></label>
      <ng-select
        #ssBarCategory
        class="ss-select"
        [class.is-open]="!!ssBarCategory.isOpen?.()"
        appendTo="body"
        ngClass="ss-dropdown-panel"
        [items]="barColumns"
        [clearable]="true"
        [searchable]="true"
        [loading]="host.isDrillDatasetColumnsLoading"
        [disabled]="!host.drillDataset"
        [placeholder]="barColumnPlaceholder()"
        [ngModel]="barCategory || null"
        (ngModelChange)="barCategory = $event || ''; onBarChange()"
      ></ng-select>
    </div>
    <!-- Hierarchical Category Axis — optional OUTER group (parent → child). -->
    <div class="control-group wf-field-drop-zone" (dragover)="host.allowFieldDrop($event)" (drop)="onBarParentFieldDrop($event)" title="Drag a column here">
      <label>Group by (parent) <span class="wf-axis-hint">(optional — hierarchical)</span></label>
      <ng-select
        #ssBarParent
        class="ss-select"
        [class.is-open]="!!ssBarParent.isOpen?.()"
        appendTo="body"
        ngClass="ss-dropdown-panel"
        [items]="barColumns"
        [clearable]="true"
        [searchable]="true"
        [loading]="host.isDrillDatasetColumnsLoading"
        [disabled]="!host.drillDataset"
        [placeholder]="barColumnPlaceholder()"
        [ngModel]="barCategoryParent || null"
        (ngModelChange)="barCategoryParent = $event || ''; onBarChange()"
      ></ng-select>
    </div>
  </div>

  <div class="control-row">
    <!-- Series = split each group into sub-bars → legend. REQUIRED for the
         split-based variants (grouped/clustered/stacked/100%); hidden for Simple. -->
    <div class="control-group wf-field-drop-zone" [class.is-required-empty]="barSeriesRequired && !barSeries" *ngIf="host.fieldEnabled('bar', 'series') && barVariantUsesSeries" (dragover)="host.allowFieldDrop($event)" (drop)="onBarSeriesFieldDrop($event)" title="Drag a column here">
      <label>Split / Legend <span class="wf-axis-hint">{{ barSeriesRequired ? '(required)' : '(optional)' }}</span></label>
      <ng-select
        #ssBarSeries
        class="ss-select"
        [class.is-open]="!!ssBarSeries.isOpen?.()"
        appendTo="body"
        ngClass="ss-dropdown-panel"
        [items]="barColumns"
        [clearable]="!barSeriesRequired"
        [searchable]="true"
        [loading]="host.isDrillDatasetColumnsLoading"
        [disabled]="!host.drillDataset"
        [placeholder]="barSeriesRequired ? 'Pick a column to split by' : barColumnPlaceholder()"
        [ngModel]="barSeries || null"
        (ngModelChange)="barSeries = $event || ''; onBarChange()"
      ></ng-select>
      <!-- <small class="control-warning" *ngIf="barSeriesRequired && !barSeries">This variant needs a Split column to render as {{ barVariant }}.</small> -->
      <small class="control-hint" *ngIf="barSeriesComesFromMeasures">Leave empty to use one series per measure below — that way a series can be a formula (add it under Measures).</small>
    </div>
  </div>

  <!-- Secondary axes — a bar chart's second VALUE axis (dual). BOTH pickers
       (Secondary X + Secondary Y) are ALWAYS shown for every variant and
       orientation; the active one folds into capabilities via
       barActiveSecondaryField (Sec. Y when Vertical, Sec. X when Horizontal). -->
  <div class="control-row">
    <div class="control-group wf-field-drop-zone" (dragover)="host.allowFieldDrop($event)" (drop)="onBarSecondaryAxisFieldDrop($event, 'x')" title="Drag a column here">
      <label>Secondary X-axis <span class="wf-axis-hint">(values)</span></label>
      <ng-select
        #ssBarSecX
        class="ss-select"
        [class.is-open]="!!ssBarSecX.isOpen?.()"
        appendTo="body"
        ngClass="ss-dropdown-panel"
        [items]="barColumns"
        [clearable]="true"
        [searchable]="true"
        [loading]="host.isDrillDatasetColumnsLoading"
        [disabled]="!host.drillDataset"
        [placeholder]="barColumnPlaceholder()"
        [ngModel]="barSecondaryXField || null"
        (ngModelChange)="barSecondaryXField = $event || ''; onBarChange()"
      ></ng-select>
    </div>
    <div class="control-group wf-field-drop-zone" (dragover)="host.allowFieldDrop($event)" (drop)="onBarSecondaryAxisFieldDrop($event, 'y')" title="Drag a column here">
      <label>Secondary Y-axis <span class="wf-axis-hint">(values)</span></label>
      <ng-select
        #ssBarSecY
        class="ss-select"
        [class.is-open]="!!ssBarSecY.isOpen?.()"
        appendTo="body"
        ngClass="ss-dropdown-panel"
        [items]="barColumns"
        [clearable]="true"
        [searchable]="true"
        [loading]="host.isDrillDatasetColumnsLoading"
        [disabled]="!host.drillDataset"
        [placeholder]="barColumnPlaceholder()"
        [ngModel]="barSecondaryYField || null"
        (ngModelChange)="barSecondaryYField = $event || ''; onBarChange()"
      ></ng-select>
    </div>
  </div>

  <!-- Per-axis label FONT WEIGHTS — highlight an axis's tick labels + title
       independently. '' = default (falls back to the Format popup's
       tile-wide "Axis label weight"). CONFIG-DRIVEN via layoutSteps entries
       in bar-column.json; options from the shared shell `fontWeights` list.
       X/Y mean the RENDERED axes (orientation decides which is category);
       the Secondary select covers the dual value axis on either side and
       only shows once one is mapped. -->
  <div class="control-row" *ngIf="host.fieldEnabled('bar', 'xAxisFontWeight') || host.fieldEnabled('bar', 'yAxisFontWeight')">
    <div class="control-group" *ngIf="host.fieldEnabled('bar', 'xAxisFontWeight')">
      <label>{{ host.fieldLabel('bar', 'xAxisFontWeight', 'X axis label weight') }}</label>
      <ng-select class="ss-select" appendTo="body" ngClass="ss-dropdown-panel" [items]="host.fontWeightOptions" bindLabel="label" bindValue="value" [clearable]="true" [searchable]="false" [ngModel]="barXAxisFontWeight" (ngModelChange)="barXAxisFontWeight = $event ?? ''; onBarChange()"></ng-select>
    </div>
    <div class="control-group" *ngIf="host.fieldEnabled('bar', 'yAxisFontWeight')">
      <label>{{ host.fieldLabel('bar', 'yAxisFontWeight', 'Y axis label weight') }}</label>
      <ng-select class="ss-select" appendTo="body" ngClass="ss-dropdown-panel" [items]="host.fontWeightOptions" bindLabel="label" bindValue="value" [clearable]="true" [searchable]="false" [ngModel]="barYAxisFontWeight" (ngModelChange)="barYAxisFontWeight = $event ?? ''; onBarChange()"></ng-select>
    </div>
  </div>
  <div class="control-row" *ngIf="barHasSecondaryAxis && host.fieldEnabled('bar', 'axis2FontWeight')">
    <div class="control-group">
      <label>{{ host.fieldLabel('bar', 'axis2FontWeight', 'Secondary axis label weight') }}</label>
      <ng-select class="ss-select" appendTo="body" ngClass="ss-dropdown-panel" [items]="host.fontWeightOptions" bindLabel="label" bindValue="value" [clearable]="true" [searchable]="false" [ngModel]="barSecondaryAxisFontWeight" (ngModelChange)="barSecondaryAxisFontWeight = $event ?? ''; onBarChange()"></ng-select>
    </div>
  </div>

  <!-- Measures = the value axis (Y when vertical, X when horizontal). Each row
       is one measure: column + draw-as + primary/secondary axis (+ reorder). -->
  <div class="wf-subsection-header">
    <span>{{ barValueAxisLabel }} <span class="wf-axis-hint">— values</span> <span class="wf-step-count">{{ barMeasures.length }}</span></span>
    <!-- Add measure only where extra measures actually render (Simple Bar,
         Comparison). Series-split variants use ONE measure, so it's hidden. -->
    <button type="button" class="add-step-button" mat-stroked-button *ngIf="barAllowsMultipleMeasures" (click)="addBarMeasure()"><mat-icon>add</mat-icon> Add measure</button>
  </div>
  <small class="control-hint" *ngIf="barVariantUsesSeries && barSeries">Series split active — the first measure supplies the value.</small>
  <small class="control-hint" *ngIf="barSeriesComesFromMeasures">No Split column picked — each measure below renders as its own series. Use the Formula Builder to add a computed column, then select it here.</small>

  <div class="wf-field-drop-zone bar-measures-zone" (dragover)="host.allowFieldDrop($event)" (drop)="onBarMeasureFieldDrop($event)">
    <div class="wf-empty" *ngIf="!barMeasures.length">No measures yet — add one, or drag a column here.</div>

    <div class="bar-measure-row" *ngFor="let measure of barMeasures; let i = index; let first = first; let last = last">
      <div class="control-row">
        <div class="control-group wf-field-drop-zone" (dragover)="host.allowFieldDrop($event)" (drop)="onBarMeasureFieldDrop($event, measure)" title="Drag a column here">
          <label>Column</label>
          <ng-select
            class="ss-select"
            appendTo="body"
            ngClass="ss-dropdown-panel"
            [items]="barColumns"
            [clearable]="true"
            [searchable]="true"
            [loading]="host.isDrillDatasetColumnsLoading"
            [disabled]="!host.drillDataset"
            [placeholder]="barColumnPlaceholder()"
            [ngModel]="measure.field || null"
            (ngModelChange)="measure.field = $event || ''; onBarMeasureChange()"
          ></ng-select>
        </div>
        <div class="control-group">
          <label>Label</label>
          <input type="text" [ngModel]="measure.label" (ngModelChange)="measure.label = $event; onBarMeasureChange()" [placeholder]="measure.field || 'Measure'" />
        </div>
      </div>
      <div class="control-row">
        <!-- Draw-as (Bar/Line/Area) — combos. Hidden for series-split variants. -->
        <div class="control-group" *ngIf="host.fieldEnabled('bar', 'renderAs') && !(barVariantUsesSeries && barSeries)">
          <label>{{ host.fieldLabel('bar', 'renderAs', 'Draw as') }}</label>
          <ng-select
            class="ss-select"
            appendTo="body"
            ngClass="ss-dropdown-panel"
            [items]="barRenderTypes"
            bindLabel="label"
            bindValue="value"
            [clearable]="true"
            [ngModel]="measure.renderAs"
            (ngModelChange)="measure.renderAs = $event ?? ''; onBarMeasureChange()"
          ></ng-select>
        </div>
        <!-- Axis (primary/secondary) — dual axis. Hidden for series-split variants. -->
        <div class="control-group" *ngIf="host.fieldEnabled('bar', 'axis') && !(barVariantUsesSeries && barSeries)">
          <label>{{ host.fieldLabel('bar', 'axis', 'Axis') }}</label>
          <ng-select
            class="ss-select"
            appendTo="body"
            ngClass="ss-dropdown-panel"
            [items]="barAxisOptions"
            bindLabel="label"
            bindValue="value"
            [clearable]="true"
            [ngModel]="measure.axis"
            (ngModelChange)="measure.axis = $event ?? ''; onBarMeasureChange()"
          ></ng-select>
        </div>
        <!-- Reorder / remove only when multiple measures apply AND more than
             one exists (a single required measure has nothing to reorder). -->
        <div class="control-group bar-measure-actions" *ngIf="barAllowsMultipleMeasures && barMeasures.length > 1">
          <button type="button" mat-icon-button (click)="moveBarMeasureUp(i)" [disabled]="first" matTooltip="Move up"><mat-icon>arrow_upward</mat-icon></button>
          <button type="button" mat-icon-button (click)="moveBarMeasureDown(i)" [disabled]="last" matTooltip="Move down"><mat-icon>arrow_downward</mat-icon></button>
          <button type="button" mat-icon-button class="row-remove-button" (click)="removeBarMeasure(measure)" matTooltip="Remove measure"><mat-icon>close</mat-icon></button>
        </div>
      </div>
    </div>
  </div>

  <!-- Reference / target lines — drawn across the value axis. -->
  <div class="wf-subsection-header">
    <span>Reference lines <span class="wf-step-count">{{ barReferenceLines.length }}</span></span>
    <button type="button" class="add-step-button" mat-stroked-button (click)="addBarReferenceLine()"><mat-icon>add</mat-icon> Add line</button>
  </div>
  <div class="bar-refline-list" *ngIf="barReferenceLines.length">
    <div class="control-row bar-refline-row" *ngFor="let line of barReferenceLines">
      <div class="control-group">
        <label>Value</label>
        <input type="number" [ngModel]="line.value" (ngModelChange)="line.value = $event; onBarReferenceLineChange()" placeholder="e.g. 100" />
      </div>
      <div class="control-group">
        <label>Label</label>
        <input type="text" [ngModel]="line.label" (ngModelChange)="line.label = $event; onBarReferenceLineChange()" placeholder="Target" />
      </div>
      <div class="control-group bar-refline-color">
        <label>Color</label>
        <input type="color" [ngModel]="line.color" (ngModelChange)="line.color = $event; onBarReferenceLineChange()" />
      </div>
      <button type="button" mat-icon-button class="bar-refline-remove row-remove-button" (click)="removeBarReferenceLine(line.id)" matTooltip="Remove line"><mat-icon>close</mat-icon></button>
    </div>
  </div>

  <!-- Value formula — computed by the BACKEND, so unlike the Formula Builder
       below it can reference a live input value via [Token] + References. -->
  <div class="control-row" *ngIf="host.fieldEnabled('bar', 'valueFormula')">
    <div class="control-group control-group-full wf-field-drop-zone" (dragover)="host.allowFieldDrop($event)" (drop)="onBarValueFormulaFieldDrop($event)">
      <label>{{ host.fieldLabel('bar', 'valueFormula', 'Value formula (computed)') }}</label>
      <input type="text" [(ngModel)]="barValueFormula" placeholder="e.g. (AVG(base_price)*(1+[Price Inflation %]/100))" />
      <small class="control-hint">
        Computed by the backend, so it can use a live input value via [Token] (add it under References below).
        Leave blank to just aggregate the Measures above. Used for any group with no override below.
      </small>
    </div>
  </div>

  <div class="control-row" *ngIf="host.fieldEnabled('bar', 'valueFormula') && barUsesValueFormula">
    <div class="control-group">
      <label>Value label</label>
      <input type="text" [(ngModel)]="barValueLabel" placeholder="Value" />
    </div>
  </div>

  <div class="control-row" *ngIf="host.fieldEnabled('bar', 'valueFormula')">
    <div class="control-group control-group-full">
      <label>Per-group formula overrides (optional)</label>
      <small class="control-hint">
        One formula per category value (matched against the {{ barCategoryAxisLabel }} field above). A group listed here ignores the shared formula.
      </small>
      <div class="target-reference-row" *ngFor="let entry of barGroupFormulas">
        <input type="text" [(ngModel)]="entry.group" placeholder="Group value, e.g. Base Options" />
        <input type="text" [(ngModel)]="entry.formula" placeholder="Formula for this group" />
        <button type="button" class="target-reference-remove" (click)="removeBarGroupFormula(entry)">Remove</button>
      </div>
      <button type="button" class="target-reference-add" (click)="addBarGroupFormula()">+ Add group formula</button>
    </div>
  </div>

  <!-- Resolves each [Token] above to another tile's current value. -->
  <ng-container *ngIf="host.fieldEnabled('bar', 'valueFormula') && host.formulaReferencesEnabledFor('BAR')">
    <ng-container *ngTemplateOutlet="referenceList; context: { id: host.componentId }"></ng-container>
  </ng-container>

  <!-- Formula Builder — row-level COMPUTED columns from the dataset's columns.
       A computed column (e.g. "Margin = ([Profit] / [Revenue]) * 100") becomes
       a mappable column in Category / Measure / Split above. Fully config-driven
       (options.formulaBuilder): text, templates, validation, and the enabled
       flag all come from JSON. -->
  <div class="dvm-formula-builder" *ngIf="formulaBuilderEnabled">
    <button type="button" class="dvm-formula-head" (click)="barFormulaAccordionOpen = !barFormulaAccordionOpen">
      <span><mat-icon class="dvm-formula-head-icon">functions</mat-icon> {{ formulaBuilderConfig.title || 'Formula Builder' }} <span class="wf-step-count">{{ barComputedColumns.length }}</span></span>
      <mat-icon>{{ barFormulaAccordionOpen ? 'expand_less' : 'expand_more' }}</mat-icon>
    </button>

    <div class="dvm-formula-body" *ngIf="barFormulaAccordionOpen">
      <p class="dvm-formula-hint" *ngIf="formulaBuilderConfig.hint">{{ formulaBuilderConfig.hint }}</p>

      <!-- Quick templates — click to fill the form below. -->
      <div class="dvm-formula-templates" *ngIf="barFormulaTemplates.length">
        <small class="control-hint">{{ formulaBuilderConfig.templatesLabel || 'Quick templates:' }}</small>
        <button
          type="button"
          class="dvm-formula-chip"
          *ngFor="let tpl of barFormulaTemplates"
          [title]="tpl.description"
          (click)="applyBarFormulaTemplate(tpl)"
        >{{ tpl.label }}</button>
      </div>

      <!-- Existing computed columns. -->
      <div class="dvm-formula-list" *ngIf="barComputedColumns.length">
        <div class="dvm-formula-item" *ngFor="let col of barComputedColumns">
          <div class="dvm-formula-item-info">
            <strong>{{ col.name }}</strong>
            <small>= {{ col.expression }}</small>
          </div>
          <button type="button" mat-icon-button class="bar-refline-remove row-remove-button" (click)="removeBarComputedColumn(col.name)" matTooltip="Remove column"><mat-icon>close</mat-icon></button>
        </div>
      </div>

      <!-- Add a new computed column. -->
      <div class="dvm-formula-add">
        <input type="text" [(ngModel)]="newBarColumnName" [placeholder]="formulaBuilderConfig.namePlaceholder || 'Column name'" />
        <input type="text" [(ngModel)]="newBarColumnExpression" [placeholder]="formulaBuilderConfig.expressionPlaceholder || 'Expression'" />
        <button type="button" class="add-step-button" mat-stroked-button (click)="addBarComputedColumn()"><mat-icon>add</mat-icon> {{ formulaBuilderConfig.addLabel || 'Add column' }}</button>
      </div>
      <small class="control-warning" *ngIf="newBarColumnError">{{ newBarColumnError }}</small>
    </div>
  </div>

      </div><!-- /Chart Data body -->
    </div><!-- /Chart Data group -->
  </div><!-- /Chart Data accordion -->

  <!-- Bar chart options — Title / Axis / Legend / Labels / Tooltip / Formatting /
       Interaction / Advanced accordions (shared template below). -->
  <ng-container [ngTemplateOutlet]="chartFormatAccordion"></ng-container>
</div>
